import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useField } from 'formik';
import { FC } from 'react';
import { useSession } from 'src/contexts/SessionContext';
import { updatePlatform } from 'src/models/platform/platform';
import { Tag, createTag, fetchTagsByType } from 'src/models/tags/tags';

import { FormAddableAutocomplete } from '../Fields/FormAutocomplete';

type TagsSelectProps = {
  name: string;
  label?: string;
  placeholder?: string;
  multiple?: boolean;
  tagType?:
    | 'client'
    | 'product'
    | 'package'
    | 'exercise'
    | 'video'
    | 'groupAssessment'
    | 'groupResource'
    | 'location'
    | 'service'
    | 'package';
};

export const FormTagsSelect: FC<TagsSelectProps> = ({
  tagType = 'client',
  ...props
}) => {
  const { session } = useSession();
  const queryClient = useQueryClient();
  const [, meta, helpers] = useField(props);
  const { value } = meta;
  const { setValue } = helpers;

  // For location tags, fetch from API; for others, use session data
  const { data: apiTags } = useQuery({
    queryKey: ['tags', tagType],
    queryFn: () => fetchTagsByType(tagType),
    enabled: tagType === 'location',
  });

  const options =
    tagType === 'location'
      ? (apiTags || []).map((tag: Tag) => ({
          id: tag.name,
          title: tag.name,
        }))
      : (session[`${tagType}Tags`] || []).map((tag) => ({
          id: tag,
          title: tag,
        }));

  return (
    <FormAddableAutocomplete
      {...props}
      options={options}
      value={value}
      onChange={
        ((value, reason, details) => {
          const newValue = [...value];
          if (reason === 'createOption') {
            const index = newValue.findIndex((v) => v === details.option);
            if (index !== -1) {
              newValue.splice(index, 1, {
                id: details.option,
                title: details.option,
              });

              if (tagType === 'location') {
                // For location tags, create via API
                createTag({
                  name: details.option,
                  tagType: 'location',
                }).then(() => {
                  // Refresh the tags list after creating a new tag
                  queryClient.invalidateQueries({
                    queryKey: ['tags', tagType],
                  });
                });
              } else {
                // For other tag types, use session-based approach
                const tagsList = session[`${tagType}Tags`] || [];
                if (!tagsList.includes(details.option)) {
                  tagsList.push(details.option);
                  session[`${tagType}Tags`] = tagsList;
                  updatePlatform(session.id, {
                    [`${tagType}Tags`]: tagsList,
                  });

                  options.push({
                    id: details.option,
                    title: details.option,
                  });
                }
              }
            }
          }
          setValue(newValue.map((v) => (typeof v === 'string' ? v : v.title)));
        }) as any
      }
    />
  );
};
